import { isValidOrgId } from "@/lib/utils";

/**
 * An array of routers that are accessible to the public
 * These routes do not require authentication
 * @type {string[]}
 */
export const publicRoutes = [
  "/",
  "/terms",
  "/privacy",
  "/auth/new-verification",
  "/auth/invite-confirmation",
  "/apfel-gmbh/dashboard",
  "/apfel-gmbh/admin",
  "/apfel-gmbh/dashboard/projects",
];

/**
 * An array of routers that are used for authentication
 * These routes will redirect signed in users to /dashboard
 * @type {string[]}
 */
export const authRoutes = [
  "/auth/sign-in",
  "/auth/sign-up",
  "/auth/register",
  "/auth/sign-out",
  "/auth/error",
  "/auth/forgot-password",
  "/auth/new-password",
];

/**
 * Automatic recovery for undecryptable/stale Auth.js cookies.
 * Reuses /auth/sign-out with Loading UI (`recover=1`), not the intentional sign-out copy.
 */
export const SESSION_RECOVER_URL = "/auth/sign-out?recover=1";

/** Short-lived marker so middleware does not re-enter recovery if a cookie survives one clear. */
export const AUTH_CLEARED_MARKER_COOKIE = "sequelizer-auth-cleared";

/**
 * The prefix for API authentication routes
 * Routes that start with this prefix are used for API authentication purposes
 * @type {string}
 */
export const apiAuthPrefix = "/api/auth";
export const superAdminPrefix = "/sa/super";

/**
 * Path after sign-in before org resolution (/{orgId} is added in DashboardRedirect).
 * @type {string}
 */
export const DEFAULT_SIGN_IN_REDIRECT_URL = "/dashboard";

/**
 * First-path segments that are reserved for redirects (no orgId).
 * Paths like /products/... or /dashboard/... (with more segments) should 404.
 */
export const RESERVED_ORG_PATH_SEGMENTS = [
  "dashboard",
  "project-templates",
  "products",
  "projects",
];

/** Open signup lands here after OAuth; sign-in still defaults to /dashboard. */
export const DEFAULT_SIGN_UP_REDIRECT_URL = "/auth/new-onboarding";

const POST_AUTH_ROOT_PATHS = [
  DEFAULT_SIGN_IN_REDIRECT_URL,
  DEFAULT_SIGN_UP_REDIRECT_URL,
  "/products",
  "/projects",
  "/project-templates",
] as const;

/** Normalize post-login callback URLs (e.g. stale `/home` cookies) to org-resolution roots. */
export function sanitizePostAuthCallbackUrl(raw: string | null): string {
  if (!raw?.startsWith("/") || raw.startsWith("//")) {
    return DEFAULT_SIGN_IN_REDIRECT_URL;
  }

  const [pathOnly, query = ""] = raw.split("?");
  if (
    POST_AUTH_ROOT_PATHS.includes(
      pathOnly as (typeof POST_AUTH_ROOT_PATHS)[number],
    )
  ) {
    return query.length > 0 ? `${pathOnly}?${query}` : pathOnly;
  }

  const firstSegment = pathOnly.split("/").filter(Boolean)[0];
  if (firstSegment && isValidOrgId(firstSegment)) {
    return query.length > 0 ? `${pathOnly}?${query}` : pathOnly;
  }

  return DEFAULT_SIGN_IN_REDIRECT_URL;
}

/** True when the current route still needs org resolution before showing the app shell. */
export function needsOrgIdResolution(
  pathname: string,
  routeOrgId: string | undefined,
): boolean {
  const pathOnly = pathname.split("?")[0];

  if (
    POST_AUTH_ROOT_PATHS.includes(
      pathOnly as (typeof POST_AUTH_ROOT_PATHS)[number],
    )
  ) {
    return true;
  }

  if (routeOrgId && !isValidOrgId(routeOrgId)) {
    return true;
  }

  if (routeOrgId && isValidOrgId(routeOrgId) && pathOnly === `/${routeOrgId}`) {
    return true;
  }

  return false;
}

/** Hide chrome on `[orgId]` routes where the segment is not a real org id (e.g. `/home`, `/dashboard`). */
export function isPseudoOrgRouteSegment(
  segment: string | undefined,
): boolean {
  return typeof segment === "string" && !isValidOrgId(segment);
}
